// Sistema centralizado de artículos del blog
// Todos los artículos se gestionan aquí y se comparten entre home y blog

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content?: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  isAI: boolean;
  views?: string;
  comments?: number;
  tags?: string[];
}

// Función para generar fechas recientes
const getRecentDate = (daysAgo: number) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Array principal de artículos - AÑADIR NUEVOS ARTÍCULOS AL INICIO
export const blogPosts: BlogPost[] = [
  {
    slug: 'google-sge-revoluciona-busquedas-diciembre-2025',
    title: 'Google SGE revoluciona las búsquedas: Cómo adaptar tu SEO en diciembre 2025',
    excerpt: 'Search Generative Experience ya está activo globalmente. Descubre las nuevas estrategias para posicionar tu contenido en la era de las respuestas generadas por IA y mantener tu tráfico orgánico.',
    content: `
      <h2>La Nueva Era de las Búsquedas con IA</h2>
      <p>Google Search Generative Experience (SGE) ha cambiado completamente la forma en que los usuarios interactúan con los resultados de búsqueda. En diciembre de 2025, SGE está activo en más de 120 países y procesa más del 40% de todas las búsquedas en Google.</p>
      
      <h2>¿Qué es Google SGE?</h2>
      <p>SGE utiliza inteligencia artificial generativa para crear respuestas completas y contextualizadas directamente en la página de resultados. En lugar de mostrar solo enlaces, Google ahora genera resúmenes completos que responden a la consulta del usuario, citando múltiples fuentes.</p>
      
      <h2>Impacto en el Tráfico Orgánico</h2>
      <p>Los estudios muestran que SGE ha reducido el CTR (Click-Through Rate) en un promedio del 18-25% para consultas informacionales. Sin embargo, las búsquedas transaccionales mantienen tasas de clic similares.</p>
      
      <h2>Estrategias de Adaptación</h2>
      <h3>1. Optimización para Citaciones</h3>
      <p>Tu objetivo ahora es ser citado en las respuestas de SGE. Para lograrlo:</p>
      <ul>
        <li>Crea contenido autoritativo y bien estructurado</li>
        <li>Usa datos verificables y cita fuentes confiables</li>
        <li>Implementa schema markup detallado</li>
        <li>Mantén tu contenido actualizado constantemente</li>
      </ul>
      
      <h3>2. Contenido de Profundidad</h3>
      <p>SGE favorece contenido que va más allá de respuestas superficiales. Crea guías completas que cubran todos los aspectos de un tema.</p>
      
      <h3>3. E-E-A-T Reforzado</h3>
      <p>La experiencia, expertise, autoridad y confiabilidad son más importantes que nunca. Google verifica la credibilidad de las fuentes antes de citarlas en SGE.</p>
      
      <h2>Oportunidades en la Era SGE</h2>
      <p>Aunque SGE presenta desafíos, también abre nuevas oportunidades:</p>
      <ul>
        <li><strong>Mayor visibilidad de marca:</strong> Ser citado en SGE aumenta el reconocimiento de marca</li>
        <li><strong>Tráfico más cualificado:</strong> Los usuarios que hacen clic después de leer SGE tienen mayor intención</li>
        <li><strong>Nuevos formatos:</strong> SGE favorece contenido multimedia y interactivo</li>
      </ul>
      
      <h2>Métricas Clave a Monitorear</h2>
      <p>En la era SGE, necesitas rastrear nuevas métricas:</p>
      <ul>
        <li>Tasa de citación en respuestas SGE</li>
        <li>Posición en las fuentes citadas</li>
        <li>CTR post-SGE</li>
        <li>Tiempo de permanencia de usuarios que vienen de SGE</li>
      </ul>
      
      <h2>Conclusión</h2>
      <p>Google SGE no es el fin del SEO, sino su evolución. Las marcas que se adapten rápidamente y creen contenido de alta calidad seguirán prosperando en este nuevo ecosistema de búsqueda.</p>
    `,
    category: 'Actualidad SEO',
    date: getRecentDate(0),
    readTime: '8 min',
    image: 'https://public.readdy.ai/ai/img_res/29a8c6aa0012aed3dafbf41cfcf22639.jpg',
    author: 'IA SEOgenia',
    isAI: true,
    views: '2.4K',
    comments: 47,
    tags: ['Google SGE', 'IA Generativa', 'SEO 2025', 'Búsquedas IA']
  },
  {
    slug: 'zero-click-searches-65-estrategias',
    title: 'Zero-Click Searches alcanzan el 65%: Estrategias de adaptación',
    excerpt: 'Las búsquedas sin clic dominan Google. Aprende a captar tráfico con featured snippets y contenido optimizado para IA.',
    content: `
      <h2>El Fenómeno Zero-Click</h2>
      <p>En diciembre de 2025, el 65% de las búsquedas en Google terminan sin un clic. Los usuarios obtienen la información que necesitan directamente en la página de resultados, sin visitar ningún sitio web.</p>
      
      <h2>¿Por qué está sucediendo?</h2>
      <p>Google ha evolucionado para proporcionar respuestas instantáneas a través de:</p>
      <ul>
        <li>Featured Snippets (fragmentos destacados)</li>
        <li>Knowledge Panels (paneles de conocimiento)</li>
        <li>People Also Ask (preguntas relacionadas)</li>
        <li>Google SGE (respuestas generadas por IA)</li>
        <li>Local Packs (resultados locales)</li>
      </ul>
      
      <h2>Impacto en el Tráfico Web</h2>
      <p>Las búsquedas zero-click han reducido el tráfico orgánico promedio en un 20-30% en los últimos dos años. Sin embargo, ciertos tipos de contenido siguen generando clics:</p>
      <ul>
        <li>Contenido transaccional (compras, reservas)</li>
        <li>Guías detalladas y tutoriales</li>
        <li>Contenido multimedia (videos, infografías)</li>
        <li>Herramientas y calculadoras interactivas</li>
      </ul>
      
      <h2>Estrategias de Adaptación</h2>
      
      <h3>1. Optimiza para Featured Snippets</h3>
      <p>Aunque no generen clics directos, los featured snippets aumentan la visibilidad de marca y la autoridad:</p>
      <ul>
        <li>Responde preguntas de forma concisa (40-60 palabras)</li>
        <li>Usa listas y tablas cuando sea apropiado</li>
        <li>Estructura el contenido con encabezados claros</li>
      </ul>
      
      <h3>2. Crea Contenido que Requiera Profundización</h3>
      <p>Diseña tu contenido para que la respuesta rápida genere curiosidad:</p>
      <ul>
        <li>Ofrece datos únicos y exclusivos</li>
        <li>Proporciona análisis profundos</li>
        <li>Incluye casos de estudio detallados</li>
      </ul>
      
      <h3>3. Diversifica tus Fuentes de Tráfico</h3>
      <p>No dependas únicamente de Google:</p>
      <ul>
        <li>Construye una audiencia en redes sociales</li>
        <li>Desarrolla una lista de email marketing</li>
        <li>Optimiza para otros motores de búsqueda (Bing, DuckDuckGo)</li>
        <li>Posiciónate en buscadores con IA (Perplexity, ChatGPT)</li>
      </ul>
      
      <h3>4. Enfócate en Búsquedas Transaccionales</h3>
      <p>Las búsquedas con intención de compra o acción siguen generando clics. Optimiza para:</p>
      <ul>
        <li>Palabras clave de compra ("comprar", "precio", "mejor")</li>
        <li>Comparativas de productos</li>
        <li>Reviews y testimonios</li>
      </ul>
      
      <h2>Métricas Alternativas al Tráfico</h2>
      <p>En la era zero-click, mide también:</p>
      <ul>
        <li><strong>Impresiones:</strong> Cuántas veces aparece tu contenido</li>
        <li><strong>Posición promedio:</strong> Tu ranking en los resultados</li>
        <li><strong>Share of Voice:</strong> Tu visibilidad vs competidores</li>
        <li><strong>Brand searches:</strong> Búsquedas de tu marca</li>
      </ul>
      
      <h2>Conclusión</h2>
      <p>Las búsquedas zero-click son la nueva realidad. En lugar de luchar contra ellas, adapta tu estrategia para maximizar la visibilidad de marca y captar el tráfico que aún está disponible.</p>
    `,
    category: 'Actualidad SEO',
    date: getRecentDate(1),
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=website%20performance%20speed%20metrics%20dashboard%20modern%20interface%20clean%20design%20with%20graphs%20and%20charts%20minimalist%20style%20with%20simple%20white%20background&width=400&height=300&seq=blog2&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '1.8K',
    comments: 32,
    tags: ['Zero-Click', 'Featured Snippets', 'Tráfico Orgánico', 'Google']
  },
  {
    slug: 'eeat-2025-experiencia-verificada',
    title: 'E-E-A-T 2025: Google añade "Experiencia Verificada" como factor',
    excerpt: 'La nueva actualización de E-E-A-T requiere verificación de experiencia real. Descubre cómo implementarla.',
    category: 'SEO Técnico',
    date: getRecentDate(3),
    readTime: '10 min',
    image: 'https://readdy.ai/api/search-image?query=structured%20data%20schema%20markup%20code%20interface%20modern%20clean%20design%20with%20JSON-LD%20syntax%20minimalist%20technical%20illustration%20with%20simple%20white%20background&width=400&height=300&seq=blog3&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '1.5K',
    comments: 28,
    tags: ['E-E-A-T', 'Autoridad', 'Confianza', 'Google']
  },
  {
    slug: 'voice-commerce-crece-300-optimiza-ecommerce',
    title: 'Voice Commerce crece 300%: Optimiza tu eCommerce para voz',
    excerpt: 'Las compras por voz explotan en 2025. Prepara tu tienda online para búsquedas conversacionales.',
    category: 'Tendencias',
    date: getRecentDate(5),
    readTime: '7 min',
    image: 'https://readdy.ai/api/search-image?query=voice%20search%20assistant%20microphone%20icon%20modern%20interface%20clean%20design%20minimalist%20style%20representing%20voice%20recognition%20technology%20with%20simple%20white%20background&width=400&height=300&seq=blog4&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '2.7K',
    comments: 52,
    tags: ['Voice Commerce', 'eCommerce', 'Búsqueda por Voz', 'Conversacional']
  },
  {
    slug: 'perplexity-ai-100m-usuarios-nuevo-canal-seo',
    title: 'Perplexity AI supera 100M usuarios: Nuevo canal de tráfico SEO',
    excerpt: 'Los motores de búsqueda con IA ganan terreno. Optimiza tu contenido para Perplexity, Claude y ChatGPT.',
    category: 'IA & SEO',
    date: getRecentDate(7),
    readTime: '9 min',
    image: 'https://readdy.ai/api/search-image?query=AI%20chatbot%20assistant%20interface%20modern%20clean%20design%20with%20chat%20bubbles%20minimalist%20style%20representing%20conversational%20AI%20technology%20with%20simple%20white%20background&width=400&height=300&seq=blog5&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '2.9K',
    comments: 61,
    tags: ['Perplexity AI', 'ChatGPT', 'Buscadores IA', 'Tráfico Alternativo']
  },
  {
    slug: 'core-web-vitals-2025-inp-reemplaza-fid',
    title: 'Core Web Vitals 2025: INP reemplaza oficialmente a FID',
    excerpt: 'Interaction to Next Paint es ahora métrica oficial. Guía completa para optimizar INP y mejorar tu ranking.',
    category: 'Performance',
    date: getRecentDate(9),
    readTime: '8 min',
    image: 'https://readdy.ai/api/search-image?query=progressive%20web%20app%20PWA%20mobile%20phone%20interface%20modern%20clean%20design%20minimalist%20style%20representing%20mobile-first%20technology%20with%20simple%20white%20background&width=400&height=300&seq=blog6&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '2.1K',
    comments: 41,
    tags: ['Core Web Vitals', 'INP', 'Performance', 'Velocidad Web']
  },
  {
    slug: 'schema-markup-contenido-ia-guia-oficial',
    title: 'Schema Markup para contenido generado por IA: Nueva guía oficial',
    excerpt: 'Google publica directrices para marcar contenido creado con IA. Implementa los nuevos schemas correctamente.',
    category: 'SEO Técnico',
    date: getRecentDate(11),
    readTime: '11 min',
    image: 'https://readdy.ai/api/search-image?query=network%20connection%20links%20nodes%20modern%20interface%20clean%20design%20minimalist%20style%20representing%20link%20building%20and%20connectivity%20with%20simple%20white%20background&width=400&height=300&seq=blog7&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '1.9K',
    comments: 35,
    tags: ['Schema Markup', 'Contenido IA', 'Datos Estructurados', 'Google']
  },
  {
    slug: 'gemini-ultra-supera-gpt4-impacto-seo',
    title: 'Gemini Ultra supera a GPT-4: Impacto en el SEO para agentes IA',
    excerpt: 'El nuevo modelo de Google está cambiando las reglas del juego. Descubre cómo optimizar tu contenido para Gemini Ultra y otros agentes conversacionales.',
    category: 'IA & SEO',
    date: getRecentDate(14),
    readTime: '10 min',
    image: 'https://readdy.ai/api/search-image?query=advanced%20AI%20neural%20network%20interface%20futuristic%20design%20with%20glowing%20nodes%20clean%20minimalist%20style%20representing%20Gemini%20AI%20technology%20with%20simple%20white%20background&width=600&height=400&seq=blog2025b&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '3.2K',
    comments: 58,
    tags: ['Gemini Ultra', 'GPT-4', 'Agentes IA', 'SEO IA']
  },
  {
    slug: 'multimodal-search-google-imagen-voz-texto',
    title: 'Multimodal Search: Google integra búsqueda por imagen, voz y texto simultánea',
    excerpt: 'La búsqueda multimodal ya es realidad. Aprende a optimizar tu contenido para consultas que combinan múltiples formatos.',
    category: 'Tendencias',
    date: getRecentDate(16),
    readTime: '10 min',
    image: 'https://readdy.ai/api/search-image?query=multimodal%20search%20interface%20with%20image%20voice%20text%20icons%20modern%20clean%20design%20minimalist%20style%20representing%20multi-format%20search%20with%20simple%20white%20background&width=600&height=400&seq=blog2025g&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '2.3K',
    comments: 44,
    tags: ['Búsqueda Multimodal', 'Visual Search', 'Voice Search', 'Google']
  },
  {
    slug: 'indexacion-selectiva-google-asegurar-rastreo',
    title: 'Indexación selectiva de Google: Cómo asegurar que tu contenido sea rastreado',
    excerpt: 'Google ahora indexa de forma más selectiva. Descubre las señales que determinan si tu contenido será incluido en el índice.',
    category: 'SEO Técnico',
    date: getRecentDate(18),
    readTime: '9 min',
    image: 'https://readdy.ai/api/search-image?query=search%20engine%20crawler%20bot%20interface%20modern%20clean%20design%20minimalist%20style%20representing%20web%20indexing%20technology%20with%20simple%20white%20background&width=600&height=400&seq=blog2025h&orientation=landscape',
    author: 'IA SEOgenia',
    isAI: true,
    views: '1.7K',
    comments: 31,
    tags: ['Indexación', 'Crawling', 'Googlebot', 'SEO Técnico']
  }
];

// Obtener artículo destacado (el más reciente)
export const getFeaturedPost = (): BlogPost => {
  return blogPosts[0];
};

// Obtener artículos recientes (excluyendo el destacado)
export const getRecentPosts = (limit: number = 6): BlogPost[] => {
  return blogPosts.slice(1, limit + 1);
};

// Obtener todos los artículos
export const getAllPosts = (): BlogPost[] => {
  return blogPosts;
};

// Obtener artículo por slug
export const getPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

// Filtrar artículos por categoría
export const getPostsByCategory = (category: string): BlogPost[] => {
  if (category === 'Todos') return blogPosts;
  return blogPosts.filter(post => post.category === category);
};

// Buscar artículos
export const searchPosts = (query: string): BlogPost[] => {
  const lowerQuery = query.toLowerCase();
  return blogPosts.filter(post => 
    post.title.toLowerCase().includes(lowerQuery) || 
    post.excerpt.toLowerCase().includes(lowerQuery)
  );
};

// Categorías disponibles
export const categories = [
  { name: 'Todos', count: blogPosts.length, icon: 'ri-apps-line' },
  { name: 'Actualidad SEO', count: blogPosts.filter(p => p.category === 'Actualidad SEO').length, icon: 'ri-newspaper-line' },
  { name: 'SEO Técnico', count: blogPosts.filter(p => p.category === 'SEO Técnico').length, icon: 'ri-code-box-line' },
  { name: 'IA & SEO', count: blogPosts.filter(p => p.category === 'IA & SEO').length, icon: 'ri-robot-line' },
  { name: 'Performance', count: blogPosts.filter(p => p.category === 'Performance').length, icon: 'ri-speed-line' },
  { name: 'Tendencias', count: blogPosts.filter(p => p.category === 'Tendencias').length, icon: 'ri-line-chart-line' },
  { name: 'Voice Search', count: blogPosts.filter(p => p.category === 'Voice Search').length, icon: 'ri-mic-line' }
];
